const DELETED_KEY = 'helport_deleted_conversations';
const HIDDEN_MESSAGES_KEY = 'helport_hidden_chat_messages';

const readHiddenMessagesMap = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(HIDDEN_MESSAGES_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const getHiddenMessagesUserKey = (user) => {
  if (!user?.email) return 'guest';
  const email = normalizeConversationEmail(user.email);
  const role = String(user.role || '').trim().toLowerCase();
  if (role === 'applicant') return email;
  return `admin:${email}`;
};

export const getHiddenMessageIds = (userKey = '') => {
  if (!userKey) return new Set();
  const map = readHiddenMessagesMap();
  const ids = Array.isArray(map[userKey]) ? map[userKey] : [];
  return new Set(ids.map((id) => String(id)));
};

export const hideMessageForUser = (userKey, messageId) => {
  if (!userKey || messageId == null) return;
  const map = readHiddenMessagesMap();
  const key = String(messageId);
  const existing = Array.isArray(map[userKey]) ? map[userKey].map(String) : [];
  if (existing.includes(key)) return;
  map[userKey] = [...existing, key];
  localStorage.setItem(HIDDEN_MESSAGES_KEY, JSON.stringify(map));
};

export const unhideMessageForUser = (userKey, messageId) => {
  if (!userKey || messageId == null) return;
  const map = readHiddenMessagesMap();
  const key = String(messageId);
  const existing = Array.isArray(map[userKey]) ? map[userKey].map(String) : [];
  map[userKey] = existing.filter((id) => id !== key);
  localStorage.setItem(HIDDEN_MESSAGES_KEY, JSON.stringify(map));
};

export const APP_TIME_ZONE = 'Asia/Manila';

const hasTimezoneInfo = (raw) => /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(raw);

const toManilaDateParts = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type) => parts.find((part) => part.type === type)?.value || '00';
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour') === '24' ? '00' : get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
};

const manilaDateKey = (date) => {
  const p = toManilaDateParts(date);
  return `${p.year}-${p.month}-${p.day}`;
};

/** Parse API/local timestamps as Philippines time so chat clocks stay stable. */
export const parseApiDateTime = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) {
    return Number.isNaN(dateStr.getTime()) ? null : dateStr;
  }

  const raw = String(dateStr).trim();
  if (!raw) return null;

  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) {
    const normalized = hasTimezoneInfo(raw) ? raw : `${raw}+08:00`;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const mysqlMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)/);
  if (mysqlMatch) {
    const timePart = mysqlMatch[2].length === 5 ? `${mysqlMatch[2]}:00` : mysqlMatch[2];
    const parsed = new Date(`${mysqlMatch[1]}T${timePart}+08:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/** Keep a stable ISO string derived from Manila-aware parsing (no wall-clock drift). */
export const normalizeMessageCreatedAt = (value) => {
  const parsed = parseApiDateTime(value);
  return parsed ? parsed.toISOString() : new Date().toISOString();
};

export const formatApiDateTime = (dateStr, lang = 'en', { showSeconds = false } = {}) => {
  const date = parseApiDateTime(dateStr);
  if (!date) return '';

  const locale = lang === 'zh' ? 'zh-CN' : undefined;
  const now = new Date();
  const isToday = manilaDateKey(date) === manilaDateKey(now);
  const timeOptions = {
    timeZone: APP_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
  };

  if (isToday) {
    return date.toLocaleTimeString(locale, timeOptions);
  }

  return date.toLocaleString(locale, {
    timeZone: APP_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: toManilaDateParts(date).year !== toManilaDateParts(now).year ? 'numeric' : undefined,
    ...timeOptions,
  });
};

export const normalizeConversationEmail = (email) => (email || '').trim().toLowerCase();

export const getConversationId = (email) => `conv:${normalizeConversationEmail(email)}`;

export const isGroupedConversationId = (id) => String(id || '').startsWith('conv:');

export const isChatMessage = (msg) => {
  const type = String(msg?.notification_type || '').trim();
  return !type || type === 'contact_message' || type === 'admin_reply';
};

const readDeletedEmails = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(DELETED_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.map(normalizeConversationEmail).filter(Boolean) : [];
  } catch {
    return [];
  }
};

export const getDeletedConversationEmails = () => new Set(readDeletedEmails());

export const markConversationsDeleted = (emails = []) => {
  const next = new Set(readDeletedEmails());
  emails.forEach((email) => {
    const normalized = normalizeConversationEmail(email);
    if (normalized) next.add(normalized);
  });
  localStorage.setItem(DELETED_KEY, JSON.stringify([...next]));
};

export const unmarkConversationDeleted = (email) => {
  const normalized = normalizeConversationEmail(email);
  if (!normalized) return;
  const next = readDeletedEmails().filter((item) => item !== normalized);
  localStorage.setItem(DELETED_KEY, JSON.stringify(next));
};

export const filterDeletedMessages = (messages = []) => {
  const deleted = getDeletedConversationEmails();
  return messages.filter((msg) => !deleted.has(normalizeConversationEmail(msg.email)));
};

const getMessageTimestamp = (msg) => {
  const latestReply = msg.replies?.length ? msg.replies[msg.replies.length - 1] : null;
  const value = latestReply?.createdAt || msg.createdAt;
  const date = parseApiDateTime(value);
  return date ? date.getTime() : 0;
};

const sortMessagesByActivity = (items = []) =>
  [...items].sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b));

export const groupMessagesIntoConversations = (messages = []) => {
  const buckets = new Map();

  messages.forEach((msg) => {
    const email = normalizeConversationEmail(msg.email);
    const key = email || `name:${(msg.name || '').trim().toLowerCase()}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(msg);
  });

  return [...buckets.entries()]
    .map(([key, sourceMessages]) => {
      const sortedSources = sortMessagesByActivity(sourceMessages);
      const latest = sortedSources[sortedSources.length - 1];
      const email = normalizeConversationEmail(latest.email)
        || (key.startsWith('conv:') ? key.slice(5) : '')
        || sourceMessages.map((m) => normalizeConversationEmail(m.email)).find(Boolean)
        || '';
      const displayName = sourceMessages.find((m) => (m.name || '').trim())?.name || latest.name || 'Applicant';

      return {
        id: email ? getConversationId(email) : `conv:${key}`,
        name: displayName,
        email,
        createdAt: latest.createdAt,
        message: latest.message,
        replies: latest.replies || [],
        _sourceMessages: sortedSources,
        _isGrouped: sortedSources.length > 1
      };
    })
    .sort((a, b) => getMessageTimestamp(b) - getMessageTimestamp(a));
};

export const groupApplicantMessages = (messages = [], userEmail = '') => {
  const normalizedEmail = normalizeConversationEmail(userEmail);
  const applicantMessages = messages.filter((msg) => {
    if (!isChatMessage(msg)) return false;
    const matchEmail = normalizeConversationEmail(msg.email) === normalizedEmail;
    const matchName = Boolean(userEmail) && normalizeConversationEmail(msg.email) === normalizedEmail;
    return matchEmail || matchName;
  });

  if (!applicantMessages.length) return [];

  const sortedSources = sortMessagesByActivity(applicantMessages);
  const latest = sortedSources[sortedSources.length - 1];

  return [{
    id: getConversationId(normalizedEmail || latest.email || 'applicant'),
    name: 'Admin',
    email: normalizedEmail || latest.email || '',
    createdAt: latest.createdAt,
    message: latest.message,
    replies: latest.replies || [],
    _sourceMessages: sortedSources,
    _isGrouped: sortedSources.length > 1
  }];
};

export const resolveEmailsFromDeleteIds = (messages = [], ids = []) => {
  const emails = new Set();

  ids.forEach((id) => {
    if (isGroupedConversationId(id)) {
      const email = id.slice(5);
      if (email) emails.add(normalizeConversationEmail(email));
      return;
    }

    const match = messages.find((msg) => msg.id === id);
    if (match?.email) {
      emails.add(normalizeConversationEmail(match.email));
    }
  });

  return [...emails];
};

export const findReplyTargetMessageId = (messages = [], conversationId) => {
  if (!isGroupedConversationId(conversationId)) {
    return conversationId;
  }

  const email = conversationId.slice(5);
  const threadMessages = messages
    .filter((msg) => normalizeConversationEmail(msg.email) === email)
    .sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b));

  if (!threadMessages.length) return null;
  return threadMessages[threadMessages.length - 1].id;
};

export const mergeApiNotifications = (existingMessages = [], apiNotifications = [], applicantEmail = '') => {
  const deleted = getDeletedConversationEmails();
  const normalizedEmail = normalizeConversationEmail(applicantEmail);
  const apiIdSet = new Set(apiNotifications.map((notif) => String(notif.id)));

  if (deleted.has(normalizedEmail)) {
    return existingMessages.filter(
      (msg) => normalizeConversationEmail(msg.email) !== normalizedEmail
    );
  }

  const localMessages = existingMessages.filter((msg) => {
    const msgEmail = normalizeConversationEmail(msg.email);
    if (msgEmail !== normalizedEmail) return true;

    const id = String(msg.id);
    // Drop optimistic local chat copies once the thread exists on the server.
    if (id.startsWith('local-')) {
      const isChat =
        msg.notification_type === 'contact_message' ||
        msg.notification_type === 'admin_reply' ||
        !msg.notification_type;
      if (isChat && apiNotifications.some((n) => (
        n.notification_type === 'contact_message' || n.notification_type === 'admin_reply'
      ))) {
        return false;
      }
      return true;
    }

    const isApiMessage = Boolean(msg.notification_type) || apiIdSet.has(id);
    if (!isApiMessage) return true;

    return apiIdSet.has(id);
  });

  const byId = new Map(localMessages.map((msg) => [String(msg.id), msg]));

  apiNotifications.forEach((notif) => {
    const key = String(notif.id);
    const previous = byId.get(key);
    byId.set(key, {
      ...notif,
      // Preserve admin/employee replies that only live in localStorage.
      replies: previous?.replies || notif.replies || []
    });
  });

  return [...byId.values()].sort((a, b) => getMessageTimestamp(b) - getMessageTimestamp(a));
};
